import React, { useState } from 'react';

import { pdf } from '@react-pdf/renderer';
import cn from 'classnames';
import { saveAs } from 'file-saver';
import { Trans, useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';

import { EventJournalItemList } from '@entities/eventJournal';
import { DateHelpers } from '@shared/helpers';
import { CustomModal, Flex, Icon, Switcher, UiButton, UiText } from '@shared/ui';
import { useExportPdfModal } from '@widgets/EventJournal/lib/hooks';
import { EventJournalItemListHeader } from '@widgets/EventJournal/ui/EventJournalItemListHeader';

import cls from './ExportPdfModal.module.css';
import { JournalPdf } from './JournalPdf/JournalPdf';

export const ExportPdfModal = () => {
    const { t } = useTranslation();

    const {
        data,
        isOpen,
        isShowCustomer,
        onShowCustomer,
        handleClose,
        dateFromQueryValue,
        dateToQueryValue,
        isLoading,
        eventsList,
        dataContext,
    } = useExportPdfModal();
    const [isDownload, setIsDownload] = useState(false);
    if (!data || !dataContext) return null;

    const estimateEntities = Object.fromEntries(
        dataContext.estimates.map((estimate) => [estimate.id, estimate])
    );

    const { dateFormatter } = DateHelpers;

    const dateFrom = dateFromQueryValue || data[0]?.date;

    const dateTo = dateToQueryValue || data[data.length - 1]?.date;

    const onDownload = async () => {
        try {
            setIsDownload(true);

            const exportName = `logs_${dataContext.project.id}_${dateFormatter({
                date: dateFromQueryValue || data[0]?.date,
                format: 'DD-MM-YY',
            })}_${dateFormatter({
                date: dateToQueryValue || data[data.length - 1]?.date,
                format: 'DD-MM-YY',
            })}`;

            const blob = await pdf(
                <JournalPdf
                    exportData={dataContext.exportData}
                    estimateEntities={estimateEntities}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    items={eventsList}
                    t={t}
                />
            ).toBlob();

            saveAs(blob, `${exportName}.pdf`);
        } catch (error) {
            console.error('PDF download error:', error);
        } finally {
            setIsDownload(false);
            await handleClose();
        }
    };

    return (
        <CustomModal
            classes={cls.ExportPdfModal}
            isOpen={isOpen}
            onHideModal={handleClose}
            isLoading={isLoading || isDownload}
            closeButtonClass={cls.CloseBtn}
        >
            <CustomModal.Header>
                <div className={cls.ExportPdfModalHeader}>
                    {t('ExportPdfModal.CustomModal.ExportPdfModalHeader')}
                    <Switcher
                        title={t('ExportPdfModal.CustomModal.Switcher')}
                        classes={cls.ExportPdfModalSwitcher}
                        isActive={isShowCustomer}
                        onClick={onShowCustomer}
                    />
                    <ReactTooltip
                        id="ExportPdf"
                        aria-haspopup="true"
                        effect="solid"
                        delayHide={200}
                        delayShow={200}
                        type="light"
                        className={cls.tooltip}
                    >
                        <p>
                            <Trans i18nKey="ExportPdfModal.CustomModal.ReactTooltip.p" />
                        </p>
                        <ul className={cls.ul}>
                            <li>{t('ExportPdfModal.CustomModal.ReactTooltip.ul.li.receipts')}</li>
                            <li>{t('ExportPdfModal.CustomModal.ReactTooltip.ul.li.overBudget')}</li>
                            <li>{t('ExportPdfModal.CustomModal.ReactTooltip.ul.li.comments')}</li>
                            <li>{t('ExportPdfModal.CustomModal.ReactTooltip.ul.li.Archiving')}</li>
                            <li>{t('ExportPdfModal.CustomModal.ReactTooltip.ul.li.request')}</li>
                            <li>{t('ExportPdfModal.CustomModal.ReactTooltip.ul.li.task')}</li>
                            <li>
                                {t('ExportPdfModal.CustomModal.ReactTooltip.ul.li.subcontractor')}
                            </li>
                        </ul>
                    </ReactTooltip>
                    <span className={cls.question} data-tip data-for="ExportPdf">
                        <Icon iconName="question" fill="currentColor" />
                    </span>
                </div>
            </CustomModal.Header>
            <CustomModal.Body>
                <section className={cn(cls.ExportPdfModalBody)}>
                    <div className={cls.ExportPdfList}>
                        <div className={cls.company}>
                            <div className={cls.companyContent}>
                                <UiText tag="span" variant="mediumPlain" weight="bold">
                                    {dataContext?.exportData?.companyName}
                                </UiText>
                                {dataContext?.exportData?.companyAddress && (
                                    <UiText tag="span" variant="caption">
                                        {dataContext?.exportData?.companyAddress}
                                    </UiText>
                                )}
                                <div className={cls.companyContacts}>
                                    {dataContext?.exportData?.companyCite && (
                                        <UiText tag="span" variant="caption">
                                            {dataContext?.exportData?.companyCite}
                                        </UiText>
                                    )}
                                    {dataContext?.exportData?.companyPhone && (
                                        <UiText tag="span" variant="caption">
                                            {dataContext?.exportData?.companyPhone}
                                        </UiText>
                                    )}
                                </div>
                            </div>
                            <div className={cls.companyLogo}>
                                <img src={dataContext?.exportData?.logoUrl || ''} />
                            </div>
                        </div>
                        <div className={cls.ExportPdfListHeader}>
                            <div className={cls.ExportPdfListHeaderTitle}>
                                <UiText weight="bold" variant="heading">
                                    {t('ExportPdfModal.CustomModal.Body.ExportPdfListHeaderTitle')}
                                </UiText>
                                <UiText weight="medium" variant="plain">
                                    {t('ExportPdfModal.CustomModal.Body.periodFrom')} {dateFrom}{' '}
                                    {t('ExportPdfModal.CustomModal.Body.periodTo')} {dateTo}
                                </UiText>
                            </div>
                            <div className={cls.grid}>
                                <UiText
                                    weight="bold"
                                    classes={cls.ExportPdfListHeaderSubTitle}
                                    variant="captionUpper"
                                >
                                    {t(
                                        'ExportPdfModal.CustomModal.Body.ExportPdfListHeaderSubTitle'
                                    )}
                                </UiText>
                                <UiText
                                    weight="bold"
                                    classes={cls.ExportPdfListHeaderSubTitle}
                                    variant="caption"
                                >
                                    {dataContext.exportData.projectName}
                                </UiText>
                                <UiText
                                    weight="bold"
                                    classes={cls.ExportPdfListHeaderSubTitle}
                                    variant="captionUpper"
                                >
                                    {t('ExportPdfModal.CustomModal.Body.address')}
                                </UiText>
                                <UiText classes={cls.ExportPdfListHeaderSubTitle} variant="caption">
                                    {dataContext.exportData.projectAddress}
                                </UiText>
                            </div>
                            <div className={cls.multiLineCell}>
                                <div className={cls.multiLineCellItem}>
                                    <UiText
                                        weight="bold"
                                        classes={cls.ExportPdfListHeaderSubTitle}
                                        variant="captionUpper"
                                    >
                                        {t('ExportPdfModal.CustomModal.Body.customer')}
                                    </UiText>
                                    <UiText
                                        classes={cls.ExportPdfListHeaderSubTitle}
                                        variant="caption"
                                    >
                                        {dataContext.exportData.customerName}
                                    </UiText>
                                </div>
                                <div className={cls.multiLineCellItem}>
                                    <UiText
                                        weight="bold"
                                        classes={cls.ExportPdfListHeaderSubTitle}
                                        variant="captionUpper"
                                    >
                                        {t('ExportPdfModal.CustomModal.Body.contractor')}
                                    </UiText>
                                    <UiText
                                        classes={cls.ExportPdfListHeaderSubTitle}
                                        variant="caption"
                                    >
                                        {dataContext.exportData.legalEntity}
                                    </UiText>
                                </div>
                            </div>
                        </div>
                        {eventsList.map((eventByDateItem, idx) => (
                            <EventJournalItemList
                                isExport
                                classes={cls.ExportPdfListItem}
                                key={`${eventByDateItem.date}-${idx}`}
                                photoEvents={
                                    eventByDateItem?.events?.photo
                                        ? [...eventByDateItem.events.photo].reverse()
                                        : []
                                }
                                textEvents={
                                    eventByDateItem?.events?.text
                                        ? [...eventByDateItem.events.text].reverse()
                                        : []
                                }
                                header={
                                    <EventJournalItemListHeader
                                        isExport
                                        classes={cls.ExportPdfListItemDate}
                                        title={dateFormatter({
                                            date: eventByDateItem.date,
                                            format: 'DD MMMM YYYY',
                                        })}
                                    />
                                }
                            />
                        ))}
                    </div>
                </section>
            </CustomModal.Body>
            <CustomModal.Footer>
                <Flex fullWidth justify="between">
                    <UiButton
                        onClick={handleClose}
                        btnColor="secondary"
                        btnText={t('button.cancel.text')}
                    />
                    <UiButton onClick={onDownload} btnText={t('button.download.text')} />
                </Flex>
            </CustomModal.Footer>
        </CustomModal>
    );
};
